import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ColDef } from 'ag-grid-community/dist/lib/entities/colDef';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import {
  AggFunc,
  BaseComponent,
  BaseEditFormComponent,
  FormlyService,
  GridRowSelect,
  Guid,
  ModalService,
  PurchaseOrderLineForAllocating,
  PurchaseOrderLineRequirementsSummaryTop,
  PurchaseOrderLineService,
  PurchaseOrderLineStockAllocation,
  PurchaseOrderLineStockIssue,
  StockItemAllocations,
  StockItemService,
  SummaryItem,
  TabGridComponent,
  YesNoModalComponent,
} from 'tms-lib';
import { PurchaseOrderLineRequirement } from '../../../models';
import {
  PurchaseOrderLineRequirementService,
  PurchaseOrderPoAllocationService,
  PurchaseOrderStockAllocationService,
  PurchaseOrderStockIssueService,
  TmsWebGridService,
} from '../../../services';
import { StockEnquiryStockAllocationsModalComponent } from '../../stock';
import { PurchaseOrderLineRequirementModalComponent } from '../purchase-order-line-requirement-modal/purchase-order-line-requirement-modal.component';

@Component({
  selector: 'app-purchase-order-line-requirements',
  templateUrl: './purchase-order-line-requirements.component.html',
  styleUrl: './purchase-order-line-requirements.component.scss',
})
export class PurchaseOrderLineRequirementsComponent extends BaseComponent implements OnInit {
  @ViewChild(BaseEditFormComponent) form: BaseEditFormComponent;
  @ViewChild(TabGridComponent, { static: true }) requirementGrid: TabGridComponent;

  @Input() purchaseOrderLineId: string;
  @Output() modelUpdated = new EventEmitter();

  model$ = new Subject<PurchaseOrderLineRequirementsSummaryTop>();
  purchaseOrderLineRequirements$ = new Subject<PurchaseOrderLineRequirement[]>();
  stockToAllocate$ = new BehaviorSubject<PurchaseOrderLineStockAllocation[]>([]);
  purchaseOrdersToAllocate$ = new BehaviorSubject<PurchaseOrderLineForAllocating[]>([]);
  issuedStock$: Observable<PurchaseOrderLineStockIssue[]>;

  columnDefsRequirements: ColDef[] = [
    ...this.gridService.checkboxRowSelect(
      'select',
      $localize`Allocate`,
      false,
      (model: PurchaseOrderLineRequirement) => +model.issued == 0,
      { aggFunc: AggFunc.sum },
    ),
    this.gridService.productSearch(
      (model: PurchaseOrderLineRequirement) =>
        this.purchaseOrderLineRequirementService.save(model).pipe(
          takeUntil(this.destroy$),
          tap(() => this.getStock(model.id, model.productId)),
        ),
      (model: PurchaseOrderLineRequirement) => model.allocated === 0 && model.issued === 0,
    ),
    this.gridService.text('productKey', $localize`Product`),
    this.gridService.text('productDescription', $localize`Description`),
    this.gridService.text('productTypeDescription', $localize`Product Type`),
    this.gridService.editableField(
      'quantity',
      $localize`Quantity Required`,
      (model: PurchaseOrderLineRequirement) => this.requirementEdited(model),
      (model: PurchaseOrderLineRequirement) => model.allocated === 0,
      { aggFunc: AggFunc.sum },
    ),
    this.gridService.numberFormat('allocated', $localize`Quantity Allocated`, { aggFunc: AggFunc.sum }),
    this.gridService.numberFormat('issued', $localize`Issued`),
  ];

  columnDefsPurchaseOrders: ColDef[] = [
    ...this.gridService.checkboxRowSelect('orderNo', $localize`Order No`, false),
    this.gridService.text('supplierKey', $localize`Supplier`),
    this.gridService.text('supplierName', $localize`Name`),
    this.gridService.text('productKey', $localize`Product`),
    this.gridService.numberFormat('balance', $localize`Order Balance`),
    this.gridService.numberFormat('allocated', $localize`Total Allocated`),
    this.gridService.numberFormat('thisAllocation', $localize`This Allocation`),
    this.gridService.date('deliveryDate', $localize`Delivery Date`),
  ];

  columnDefsStock: ColDef[] = [
    ...this.gridService.checkboxRowSelect(
      'stockNo',
      $localize`Stock No`,
      false,
      (model: PurchaseOrderLineStockAllocation) => model.issued === 0,
    ),
    this.gridService.text('batchNo', $localize`Batch Number`),
    this.gridService.text('gradeDescription', $localize`Grade`),
    this.gridService.numberFormat('balance', $localize`Current Quantity`, { aggFunc: AggFunc.sum }),
    this.gridService.clickableField(
      'totalAllocated',
      $localize`Total Allocated`,
      (model: PurchaseOrderLineStockAllocation) => this.showAllocations(model),
      (model: PurchaseOrderLineStockAllocation) => model.totalAllocated > 0,
      { aggFunc: AggFunc.sum },
    ),
    this.gridService.numberFormat('freeStock', $localize`Available`, { aggFunc: AggFunc.sum }),
    this.gridService.editableField(
      'allocated',
      $localize`This Allocation`,
      (model: PurchaseOrderLineStockAllocation) => this.allocatedEdited(model),
      (model: PurchaseOrderLineStockAllocation) => +model.allocated > 0,
      { aggFunc: AggFunc.sum },
    ),
    this.gridService.text('warehouseKey', $localize`Warehouse`),
    this.gridService.text('locationKey', $localize`Location`),
  ];

  columnDefsIssues: ColDef[] = [
    this.gridService.text('stockNo', $localize`Stock Number`),
    this.gridService.text('productKey', $localize`Product`),
    this.gridService.text('productDescription', $localize`Description`),
    this.gridService.text('productType', $localize`Product Type`),
    this.gridService.text('batchNo', $localize`Batch Number`),
    this.gridService.numberFormat('issued', $localize`Issued Quantity`, { aggFunc: AggFunc.sum }),
    this.gridService.text('warehouseKey', $localize`Warehouse`),
    this.gridService.text('locationKey', $localize`Location`),
  ];

  additionalFields: FormlyFieldConfig[];

  private purchaseOrderLineRequirementId: string = '';
  private productId: string = '';
  private checkAllocated: number;
  private checkIssued: number;
  private checkRequired: number;

  constructor(
    private purchaseOrderLineRequirementService: PurchaseOrderLineRequirementService,
    private purchaseOrderLineService: PurchaseOrderLineService,
    private purchaseOrderStockAllocationService: PurchaseOrderStockAllocationService,
    private purchaseOrderStockIssueService: PurchaseOrderStockIssueService,
    private purchaseOrderPoAllocationService: PurchaseOrderPoAllocationService,
    private stockItemService: StockItemService,
    private formlyService: FormlyService,
    private gridService: TmsWebGridService,
    private modalService: ModalService,
  ) {
    super();
  }

  ngOnInit() {
    this.getModel();
    this.getFields();
    this.stockToAllocate(this.purchaseOrderLineId, '', true);
    this.getStockIssued();

    this.purchaseOrderLineRequirementService.refreshDetails
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.getModel());
  }

  summaryItems(model: PurchaseOrderLineRequirementsSummaryTop): SummaryItem[] {
    return [
      { label: $localize`Product`, value: model.productKey },
      { label: '', value: model.productDescription },
      { label: $localize`Order Quantity`, value: model.quantity },
      { label: $localize`Requirement Quantity`, value: model.requirementQuantity },
      { label: $localize`Allocated`, value: model.allocated },
      { label: $localize`Issued`, value: model.issued },
    ];
  }

  getFields() {
    this.additionalFields = [
      this.formlyService.button(
        'Issue Stock',
        () => this.issueStock(),
        {
          btnClass: 'success',
          iconClass: 'fa fa-sign-out-alt',
        },
        undefined,
        { 'props.disabled': () => this.checkAllocated == 0 },
      ),
      this.formlyService.button(
        'Un-Issue Stock',
        () => this.unIssueStock(),
        {
          btnClass: 'info text-white',
          iconClass: 'fa fa-sign-in-alt',
        },
        undefined,
        { 'props.disabled': () => this.checkIssued == 0 },
      ),
    ];
  }

  getModel() {
    this.purchaseOrderLineRequirementService
      .createPurchaseOrderRequirements(this.purchaseOrderLineId, this.purchaseOrderLineRequirementId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((model: PurchaseOrderLineRequirement[]) => {
        this.purchaseOrderLineRequirements$.next(model);
      });

    this.purchaseOrderLineService
      .requirementsSummaryTop(this.purchaseOrderLineId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((model: PurchaseOrderLineRequirementsSummaryTop) => {
        this.model$.next(model);
        this.checkAllocated = model.allocated;
        this.checkIssued = model.issued;
        this.checkRequired = model.requirementQuantity;
      });
  }

  rowRequirementSelected(event: GridRowSelect) {
    if (event.selected) {
      this.getStock(event.model.id, event.model.productId);
    }
  }

  rowAllocationSelected(event: GridRowSelect) {
    if (!this.purchaseOrderLineRequirementId) {
      this.purchaseOrderLineRequirementId = this.requirementGrid.rowData[0].id;
      this.productId = this.requirementGrid.rowData[0].productId;
    }

    (event.selected
      ? this.purchaseOrderStockAllocationService.allocate(this.purchaseOrderLineRequirementId, event.model.stockItemId)
      : this.purchaseOrderStockAllocationService.delete(event.model.id)
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateAllocateSuccess(this.purchaseOrderLineRequirementId));
  }

  rowPurchaseOrderSelected(event: GridRowSelect) {
    const allocateOrDeallocate = event.selected
      ? this.purchaseOrderPoAllocationService.allocate(event.model.id, this.requirementGrid.grid.selectedIds[0])
      : this.purchaseOrderPoAllocationService.delete(event.model.purchaseOrderPoAllocationId);

    allocateOrDeallocate.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateSuccess());
  }

  updateAllocateSuccess(purchaseOrderLineRequirementId?: string) {
    this.getStockToAllocate(
      purchaseOrderLineRequirementId ? this.purchaseOrderLineId : undefined,
      purchaseOrderLineRequirementId,
      this.productId,
    );
    this.getModel();

    this.updateSuccess();
  }

  updateIssueUnIssueSuccess() {
    this.getModel();
    this.getStockIssued();
  }

  updateSuccess() {
    this.purchaseOrderLineService.refreshDetails.next();
    this.purchaseOrdersToAllocate(this.productId, this.purchaseOrderLineId);
  }

  create() {
    this.openPurchaseOrderLineRequirement(undefined, this.purchaseOrderLineId);
  }

  delete(model: PurchaseOrderLineRequirement) {
    this.purchaseOrderLineRequirementService
      .delete(model.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.getModel());
  }

  deletePoAllocation(model: PurchaseOrderLineForAllocating) {
    this.purchaseOrderPoAllocationService
      .delete(model.purchaseOrderPoAllocationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateSuccess());
  }

  private getStock(purchaseOrderLineRequirementId: string, productId: string) {
    this.purchaseOrderLineRequirementId = purchaseOrderLineRequirementId;
    this.productId = productId;

    this.getStockToAllocate(
      this.requirementGrid.grid.selectedIds.length > 0 ? this.purchaseOrderLineId : undefined,
      this.requirementGrid.grid.selectedIds.length > 0 ? this.purchaseOrderLineRequirementId : undefined,
      this.requirementGrid.grid.selectedIds.length > 0 ? this.productId : undefined,
    );
  }

  private allocatedEdited(model: PurchaseOrderLineStockAllocation) {
    this.purchaseOrderStockAllocationService
      .updatePurchaseOrderStockAllocation(model.id, model.allocated)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateAllocateSuccess(this.purchaseOrderLineRequirementId));
  }

  private getStockIssued() {
    this.issuedStock$ = this.stockItemService.purchaseOrderStockIssues(this.purchaseOrderLineId);
  }

  private getStockToAllocate(purchaseOrderLineId: string, purchaseOrderLineRequirementId: string, productId: string) {
    if (!purchaseOrderLineId && !purchaseOrderLineRequirementId && !productId) {
      this.stockToAllocate$.next([]);
      this.purchaseOrdersToAllocate$.next([]);
      return;
    }

    this.stockToAllocate(purchaseOrderLineId, purchaseOrderLineRequirementId, false);
  }

  private issueStock() {
    const { modal, component } = this.modalService.openSmall<YesNoModalComponent>(YesNoModalComponent);
    component.header = $localize`Issue Stock`;
    component.message =
      this.checkAllocated < this.checkRequired
        ? 'Allocated quantity is less that requirement. Do you want to issue the allocated stock?'
        : 'Issue all allocated stock?';
    modal.result.then(
      (yes: boolean) => {
        if (yes) {
          this.updateStockIssues();
        }
      },
      () => {},
    );
  }

  private openPurchaseOrderLineRequirement(id: string, purchaseOrderLineId: string) {
    const { modal, component } = this.modalService.openMedium<PurchaseOrderLineRequirementModalComponent>(
      PurchaseOrderLineRequirementModalComponent,
    );
    component.id = id;
    component.purchaseOrderLineId = purchaseOrderLineId;
    modal.result.then(
      () => this.getModel(),
      () => {},
    );
  }

  private requirementEdited(model: PurchaseOrderLineRequirement) {
    this.purchaseOrderLineRequirementService
      .save(model)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateAllocateSuccess(model.id));
  }

  private showAllocations(model: PurchaseOrderLineStockAllocation) {
    this.stockItemService
      .stockItemAllocations(model.stockItemId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((stockItem: StockItemAllocations) => {
        const { modal, component } = this.modalService.openLarge<StockEnquiryStockAllocationsModalComponent>(
          StockEnquiryStockAllocationsModalComponent,
        );
        component.model = stockItem;
        component.showDelete = false;
        modal.result.then(
          () => {},
          () => {},
        );
      });
  }

  private stockToAllocate(
    purchaseOrderLineId: string,
    purchaseOrderLineRequirementId: string,
    initialDisplay: boolean,
  ) {
    this.stockItemService
      .purchaseOrderStockAllocation(purchaseOrderLineId, purchaseOrderLineRequirementId, initialDisplay)
      .pipe(takeUntil(this.destroy$))
      .subscribe((allocations: PurchaseOrderLineStockAllocation[]) => {
        this.stockToAllocate$.next(allocations);
        this.productId = allocations[0]?.productId;
        this.purchaseOrdersToAllocate(this.productId ?? Guid.empty, this.purchaseOrderLineId);
      });
  }

  private purchaseOrdersToAllocate(productId: string, purchaseOrderLineId: string) {
    this.purchaseOrderLineService
      .getPurchaseOrdersForAllocating(productId, purchaseOrderLineId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((purchaseAllocations: PurchaseOrderLineForAllocating[]) =>
        this.purchaseOrdersToAllocate$.next(purchaseAllocations),
      );
  }

  private unIssueStock() {
    const { modal, component } = this.modalService.openSmall<YesNoModalComponent>(YesNoModalComponent);
    component.header = $localize`Un-Issue Stock`;
    component.message = $localize`Do you want to un-issue the stock?`;
    modal.result.then(
      (yes: boolean) => {
        if (yes) {
          this.updateUnIssueStock();
        }
      },
      () => {},
    );
  }

  private updateStockIssues() {
    this.purchaseOrderStockIssueService
      .issue(this.purchaseOrderLineId, undefined)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateAllocateSuccess(undefined);
        this.updateIssueUnIssueSuccess();
      });
  }

  private updateUnIssueStock() {
    this.purchaseOrderStockIssueService
      .unIssue(this.purchaseOrderLineId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateIssueUnIssueSuccess();
        this.stockToAllocate(this.purchaseOrderLineId, '', true);
        this.getModel();
        this.updateSuccess();
      });
  }
}
